import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import { RootState } from './reducers';
import { batch, useSelector as reduxUseSelector, useDispatch } from 'react-redux';
import {
  hydrateCommonReducer,
  saveCanonicalData,
  saveCatalogCategories,
  saveCatalogItemsBasicsByStoreId,
  saveCatalogTags,
  saveCollections,
  saveCouponsList,
  saveFeatureLocks,
  saveJsonWidgetsData,
  saveStoreInfo,
  saveStorePolicies,
  updateAdditionalPageReducer,
} from './actions';
import { IS_SERVER } from '@/utils/checkRenderEnv';
import { SSRSelectorContext } from './useSSRSelector';
import { useContext } from 'react';
import { PATH } from 'src/utils/routes';
import {
  storeDataWithGlobalColor,
  storeDataWithUpdatedTheme,
} from 'src/utils/storeDataWithGlobalColor';

type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

const ssrStoreFactory = () => {
  let _store: Record<string, RootState> = {};

  const updateReduxStore = (ssrData: RecursivePartial<RootState>, dispatch) => {
    if (!IS_SERVER && dispatch) {
      const {
        storeReducer,
        additionalPagesReducer,
        jsonWidgetsReducer,
        promoReducer,
        catalogReducer,
        storePolicyReducer,
        commonReducer,
      } = ssrData;

      batch(() => {
        if (storeReducer?.store) {
          if (window.location.pathname.includes(PATH.WEBSITE_BUILDER)) {
            dispatch(
              saveStoreInfo({
                ...storeReducer.store,
                theme: {
                  ...storeReducer.store.theme,
                  components:
                    jsonWidgetsReducer.themeComponents ||
                    storeReducer.store.theme.components,
                },
              })
            );
          } else if (window.location.pathname.includes(PATH.BUILDER_PREVIEW)) {
            const updatedStoreData = storeDataWithUpdatedTheme(
              storeReducer.store,
              jsonWidgetsReducer.themeComponents
            );
            const updatedStoreDataWithColor = storeDataWithGlobalColor(
              updatedStoreData,
              jsonWidgetsReducer.globalStyle
            );
            dispatch(saveStoreInfo(updatedStoreDataWithColor));
          } else {
            const updatedStoreData = storeDataWithGlobalColor(
              storeReducer.store,
              jsonWidgetsReducer.globalStyle
            );
            dispatch(saveStoreInfo(updatedStoreData));
          }
        }
        if (additionalPagesReducer) {
          dispatch(updateAdditionalPageReducer(additionalPagesReducer));
        }
        if (jsonWidgetsReducer?.widgets) {
          const jsonWidgetsReducerClone = cloneDeep(jsonWidgetsReducer);
          if (Object.keys(jsonWidgetsReducerClone.widgetTemplateLibraryMap)?.length) {
            jsonWidgetsReducerClone.widgetTemplateLibraryList = Object.values(
              jsonWidgetsReducerClone.widgetTemplateLibraryMap
            );
            jsonWidgetsReducerClone.widgetTemplateLibraryList =
              jsonWidgetsReducerClone.widgetTemplateLibraryList.sort(
                (widgetA: any, widgetB: any) => {
                  if (widgetA.type === widgetB.type) {
                    return widgetA.name < widgetB.name ? -1 : 1;
                  } else {
                    return widgetA.type < widgetB.type ? -1 : 1;
                  }
                }
              );
          }
          dispatch(saveJsonWidgetsData(jsonWidgetsReducerClone));
        }
        if (promoReducer?.couponsList) {
          dispatch(saveCouponsList(promoReducer.couponsList));
        }
        if (catalogReducer) {
          const { categories, collections, productTags, catalog_items, canonicalData } =
            catalogReducer;
          if (categories) {
            dispatch(saveCatalogCategories(categories));
          }
          if (collections) {
            dispatch(saveCollections(collections));
          }
          if (productTags) {
            dispatch(saveCatalogTags(productTags));
          }
          if (catalog_items) {
            dispatch(saveCatalogItemsBasicsByStoreId(catalog_items));
          }
          if (canonicalData) {
            dispatch(saveCanonicalData(canonicalData));
          }
        }
        if (storePolicyReducer) {
          dispatch(
            saveStorePolicies(
              storePolicyReducer.data,
              storePolicyReducer.isFetchedFromSSR
            )
          );
        }
        if (commonReducer) {
          if (commonReducer.featureLocksData?.featureLocks) {
            dispatch(saveFeatureLocks(commonReducer.featureLocksData.featureLocks));
          }
          delete commonReducer.featureLocksData;
          dispatch(hydrateCommonReducer(commonReducer));
        }
      });
    }
  };

  /**
   * To be called inside function component, and at top level with out conditions, as it uses hooks.
   * @param store SSR Store data
   */
  // eslint-disable-next-line new-cap
  const useSetSSRStore = (store: RootState) => {
    const { domain } = useContext(SSRSelectorContext);
    const dispatch = useDispatch();
    if (!store) {
      return;
    }
    // eslint-disable-next-line new-cap
    _store[domain] = store;
    if (!IS_SERVER && dispatch) {
      updateReduxStore(store, dispatch);
    }
  };

  const updateSSRStore = (partialStore: RecursivePartial<RootState>, domain: string) => {
    _store[domain] = merge(partialStore, _store[domain]);
  };

  const getSSRStore = (domain: string): RootState => {
    return _store[domain];
  };

  const clearSSRStore = (domain?: string) => {
    if (domain) {
      _store[domain] = null;
    } else {
      _store = {};
    }
  };

  function useSSRSelector<TSelected = unknown>(
    selector: (state: RootState) => TSelected,
    equalityFn?: (left: TSelected, right: TSelected) => boolean
  ): TSelected {
    const { domain } = useContext(SSRSelectorContext);
    if (typeof window === 'undefined') {
      try {
        return selector(getSSRStore(domain));
      } catch {
        return reduxUseSelector<RootState, TSelected>(selector, equalityFn);
      }
    }
    return reduxUseSelector<RootState, TSelected>(selector, equalityFn);
  }

  return {
    useSetSSRStore,
    updateSSRStore,
    getSSRStore,
    clearSSRStore,
    useSSRSelector,
    updateReduxStore,
  };
};

const ssrStore = ssrStoreFactory();
export const useSetSSRStore = ssrStore.useSetSSRStore;
export const updateSSRStore = ssrStore.updateSSRStore;
export const getSSRStore = ssrStore.getSSRStore;
export const clearSSRStore = ssrStore.clearSSRStore;
export const useSSRSelector = ssrStore.useSSRSelector;
